// Payment sources — the cards this rider pays with.
//
// Was a collapsed accordion of MUI `ListItem`s with a tinted "Default" chip. Now
// hairline rows in a `SectionCard`, and the chip is one word of secondary text: three
// tinted pills in a three-row list read as three alerts, which is not what "this is
// the default card" is. The per-card menu (delete / set as default) is unchanged —
// it is the only write on this card and it stays exactly where it was.
import { useCallback, useState } from 'react';
import { Stack, Typography, Box, Menu, MenuItem, IconButton, CircularProgress } from '../../../ui/core';
import { MoreVertIcon, DollarBadgeIcon } from '../../../ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { capitalize } from 'lodash';
import { useToast } from '../../../hooks';
import { getPaymentSources, removePaymentSource, setDefaultPaymentSource } from '../../../redux/Users/actions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  TrailingMeta,
  Meta,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';

import AmexSvg from '../../../assets/payment/amex.svg';
import DiscoverSvg from '../../../assets/payment/discover.svg';
import MaestroSvg from '../../../assets/payment/maestro.svg';
import McSvg from '../../../assets/payment/mc.svg';
import VisaSvg from '../../../assets/payment/visa.svg';
import CardSvg from '../../../assets/payment/card.svg';

const BRAND_ART = {
  amex: AmexSvg,
  discover: DiscoverSvg,
  maestro: MaestroSvg,
  mastercard: McSvg,
  visa: VisaSvg,
};

const brandArt = (brand) => BRAND_ART[String(brand || '').toLowerCase()] || CardSvg;

/** `3/2027`, and `03/2027` — zero-padded, because a mixed-width column reads ragged. */
const expiry = (source) => `${String(source.exp_month).padStart(2, '0')}/${source.exp_year}`;

const CardRow = ({ source, waiting, onDelete, onSetDefault, first }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);

  return (
    <Row
      first={first}
      label={
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box component="img" height="17" width="25" src={brandArt(source.brand)} alt="" sx={{ flexShrink: 0 }} />
          <span>•••• {source.last4}</span>
        </Stack>
      }
      meta={`Expires ${expiry(source)}`}
      right={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {source.isDefault && <Meta>Default</Meta>}
          <IconButton
            size="small"
            disabled={waiting}
            id={`${source.id}-menu-button`}
            aria-label={`Actions for card ending ${source.last4}`}
            aria-controls={menuOpen ? `${source.id}-menu` : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ width: 26, height: 26, color: 'text.secondary' }}
          >
            {waiting ? <CircularProgress size="15px" /> : <MoreVertIcon sx={{ fontSize: 17 }} />}
          </IconButton>
          <Menu
            id={`${source.id}-menu`}
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            MenuListProps={{ 'aria-labelledby': `${source.id}-menu-button` }}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                onDelete(source);
              }}
            >
              <Typography color="error">Delete card</Typography>
            </MenuItem>
            {!source.isDefault && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  onSetDefault(source);
                }}
              >
                <Typography>Set as default</Typography>
              </MenuItem>
            )}
          </Menu>
        </Stack>
      }
    />
  );
};

const PaymentSourcesCard = ({ user }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.users.selection.paymentSources);
  const [waitingFor, setWaitingFor] = useState(null);
  const t = useToast();

  // Was `useEffect(…, [])` — the cards of whoever was opened first.
  const load = useCallback(() => getPaymentSources(user.id)(dispatch), [user.id, dispatch]);
  const [status, retry] = useSectionFetch(load);

  const deleteSource = async (source) => {
    setWaitingFor(source.id);
    const { success } = await removePaymentSource(user.id, source.id)(dispatch);
    setWaitingFor(null);
    success ? t.success(`${capitalize(source.object)} as been deleted`) : t.error(`Something went wrong`);
  };

  const setDefaultSource = async (source) => {
    setWaitingFor(source.id);
    const { success } = await setDefaultPaymentSource(user.id, source.id)(dispatch);
    setWaitingFor(null);
    success ? t.success(`${capitalize(source.object)} has been set as default`) : t.error(`Something went wrong`);
  };

  const cards = data ? data.filter((source) => source.object === 'card') : null;

  return (
    <SectionCard
      title="Payment sources"
      icon={<DollarBadgeIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        cards ? (
          <TrailingMeta>
            <Meta>{cards.length} on file</Meta>
          </TrailingMeta>
        ) : null
      }
    >
      {status === 'loading' && <SectionSkeleton />}
      {/* Stripe is a third party and this is the section that says whether someone can be
          charged — "we could not ask Stripe" and "they have no card" have to look
          different, or support tells a rider to add a card they already have. */}
      {status === 'failed' && <SectionError onRetry={retry}>Couldn&rsquo;t load payment sources.</SectionError>}
      {status === 'done' && !cards?.length && <EmptyRows>No cards on file.</EmptyRows>}
      {status === 'done' &&
        cards?.map((source, i) => (
          <CardRow
            key={source.id}
            first={i === 0}
            source={source}
            waiting={waitingFor === source.id}
            onDelete={deleteSource}
            onSetDefault={setDefaultSource}
          />
        ))}
    </SectionCard>
  );
};

export default PaymentSourcesCard;
